// ============================================================
// WHAT IS UNDER THE POINTER, ON THE GPU MAP (perf overhaul, Stage 2b).
//
// On the svg map a press reads the face it landed on (`data-solid-id`): the browser's hit test finds the topmost
// polygon, which the painter's sort made the nearest. The GPU map draws no faces in the DOM, so the same question is
// asked of the world: the pointer's line of sight - from the eye (or from behind the screen, under the parallel
// camera) into the scene - and the solid it enters first. The tests hold this equal to the svg's topmost face on a
// grid of pixels under both cameras.
//
// The pixel is in the projection's own space (SCENE_VIEW: utils/scenePointer `pointerToScene`). Under either camera,
// a pixel's point on the plane through the target facing the eye is the same world point (perspective scales by
// exactly 1 there), which is what anchors the line.
//
// Pure. Zero React, zero three.js.
// ============================================================
import type { Camera, Viewport } from '../../battlefield/projection';
import type { Solid, Vec3 } from '../../battlefield/types';
import { entryInto } from './worldOcclusion';
import { FAR_M, lens, plus } from './sight';
import type { Segment } from './sight';

const DEG = Math.PI / 180;

/** The world point the pixel shows on the plane through the target that faces the eye. */
function onTargetPlane(sx: number, sy: number, camera: Camera, view: Viewport): Vec3 {
  const t = camera.yaw * DEG;
  const f = camera.pitch * DEG;
  const u = (sx - view.width / 2) / camera.zoom;
  const v = (sy - view.height / 2) / camera.zoom;
  // project()'s axes: u across (cos t, 0, -sin t); towards the eye (sin t, 0, cos t); up. On this plane the
  // distance along the sight line is 0, so towards = v sin f and height = -v cos f.
  return {
    x: camera.target.x + u * Math.cos(t) + v * Math.sin(f) * Math.sin(t),
    y: (camera.lift ?? 0) - v * Math.cos(f),
    z: camera.target.z - u * Math.sin(t) + v * Math.sin(f) * Math.cos(t),
  };
}

/** The pixel's line of sight into the scene, as far as anything is drawn: from the near plane (or far behind). */
function screenRay(sx: number, sy: number, camera: Camera, view: Viewport): Segment {
  const p = onTargetPlane(sx, sy, camera, view);
  const { into, eye, near } = lens(camera, view);
  if (!eye) return { a: plus(p, into, -FAR_M), b: plus(p, into, FAR_M) };
  const toward = { x: p.x - eye.x, y: p.y - eye.y, z: p.z - eye.z };
  const length = Math.hypot(toward.x, toward.y, toward.z);
  const dir = { x: toward.x / length, y: toward.y / length, z: toward.z / length };
  // The near plane lies `near` deep along the sight axis: further along a slanted ray.
  const slant = dir.x * into.x + dir.y * into.y + dir.z * into.z;
  return { a: plus(eye, dir, near / slant), b: plus(eye, dir, FAR_M) };
}

/**
 * The solid the pointer's line of sight enters first, or null: what a press on the svg's faces would have found.
 * Hidden terrain counts only with `includeHidden` (the GM sees it drawn); a solid the line starts inside - the eye's
 * near plane within it - is skipped, as the svg clips the faces it would have hit.
 */
export function pickSolid(
  sx: number,
  sy: number,
  solids: readonly Solid[],
  camera: Camera,
  view: Viewport,
  includeHidden = true,
): Solid | null {
  const ray = screenRay(sx, sy, camera, view);
  let best: Solid | null = null;
  let nearest = Infinity;
  for (const solid of solids) {
    if (solid.hidden && !includeHidden) continue;
    const t = entryInto(ray, solid);
    if (t !== null && t > 1e-9 && t < nearest) {
      nearest = t;
      best = solid;
    }
  }
  return best;
}
